import {
  Box,
  FormControlLabel,
  Radio,
  RadioGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import Grid2 from '@thirdparty-framework/mui/material/Grid';
import {
  ReadOnly,
  useEntityPermission,
  useMasterData,
} from '@ui-framework/index';
import { observer } from 'mobx-react';
import React, { forwardRef, useEffect, useState } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import PhoneInput from 'react-phone-number-input/input';
import 'react-phone-number-input/style.css';
import getSMSSubscriptionAttributes from './lib/getSMSSubscriptionAttributes';
import normalizeUS from './lib/normalizeUS';
import formatUSPhoneNumber from './lib/formatUSPhoneNumber';
import PhoneTextField from './components/PhoneTextField';
import UserSMSDisclosure from './UserSMSDisclosure';
import ServiceProviderSMSDisclosure from './ServiceProviderSMSDisclosure';
import InsightChipWithToolTip from './InsightChipWithToolTip';

/*
 insightPurchased : Send "field name" from from Schema JSON and value comes from form values
 type: Send from Schema JSON  attributeas "INSIGHT_NUMBER_ONLY" or "INSIGHT_FULL" if phonenumber is insight number
 smsSubscriptionStatus:
  - If type "INSIGHT_NUMBER_ONLY" send "smsSubscriptionStatus" as component prop and value needs to be set on component prop
  - If type "INSIGHT_FULL" send "smsSubscriptionStatus" Send "field name" from from Schema JSON and value comes from form values

{
  "row": 1,
  "name": "customerPhone",
  "type": "INSIGHT_FULL",
  "label": "Phone",
  "control": "PhoneNumber",
  "required": true,
  "dependencies": {
  "smsOptedIn": "customerPhoneSmsOptedIn",
  "insightPurchased": "insightPurchased",
  "smsSubscriptionStatus": "customerPhoneSmsStatus"
  }
*/
const PhoneNumber = forwardRef(({ ...props }, ref) => {
  const { control, getValues, setValue } = useFormContext();
  const { isHidden, isReadOnly } = useEntityPermission();
  const masterData = useMasterData();
  const [showDisclosure, setShowDisclosure] = useState(false);

  const phoneTypeInsight = [
    'INSIGHT_NUMBER_ONLY',
    'INSIGHT_FULL',
    'INSIGHT_MANAGED',
  ].includes(props?.field?.type);

  let smsSubscriptionStatus = null;
  let insightPurchased = false;
  let insightPurchasedAndTypeInsight = false;
  const { dependencies = null } = props.field;

  if (phoneTypeInsight) {
    if (props?.field?.type === 'INSIGHT_NUMBER_ONLY') {
      smsSubscriptionStatus = props?.smsSubscriptionStatus || null;
    }
    if (props?.field?.type === 'INSIGHT_FULL') {
      smsSubscriptionStatus =
        getValues(dependencies?.smsSubscriptionStatus || 'undefined') || null;
    }

    insightPurchased = dependencies?.insightPurchased
      ? getValues(dependencies?.insightPurchased || 'undefined')
      : false;

    insightPurchasedAndTypeInsight = phoneTypeInsight && insightPurchased;
  }

  const currentvalue = getValues(props?.field?.name);

  const Disclosure =
    props?.field?.disclosure === 'SERVICE_PROVIDER'
      ? ServiceProviderSMSDisclosure
      : UserSMSDisclosure;

  if (isReadOnly(props?.permission, currentvalue)) {
    let status = null;
    if (insightPurchasedAndTypeInsight && smsSubscriptionStatus !== null) {
      status = getSMSSubscriptionAttributes(
        smsSubscriptionStatus,
        formatUSPhoneNumber(masterData.saPhoneNumber),
      );
    }

    let managedPhoneAcceptsSMS = false;
    if (insightPurchased && props?.field?.type === 'INSIGHT_MANAGED') {
      const cpSMSSubscriptionStatus =
        getValues(dependencies?.customerPhoneSmsStatus || 'undefined') || null;
      const spaSMSSubscriptionStatus =
        getValues(
          dependencies?.customerPhoneAlternateSmsSubscriptionStatus ||
            'undefined',
        ) || null;
      managedPhoneAcceptsSMS = [
        cpSMSSubscriptionStatus,
        spaSMSSubscriptionStatus,
      ].includes('SUBSCRIBED');
    }

    let phoneNumberToDisplay = currentvalue
      ? formatUSPhoneNumber(currentvalue)
      : currentvalue;

    if (props?.field?.type === 'INSIGHT_MANAGED' && !phoneNumberToDisplay) {
      phoneNumberToDisplay = 'Not Active';
    }

    return (
      <Box>
        <Box>
          <ReadOnly name={props?.name || props?.field?.name}>
            {phoneNumberToDisplay}
            {insightPurchasedAndTypeInsight &&
              smsSubscriptionStatus !== null && (
                <InsightChipWithToolTip sx={{ ml: 1 }} status={status} />
              )}
            {insightPurchased && props?.field?.type === 'INSIGHT_FULL' && (
              <Grid2 container sx={{ mt: 1 }}>
                <Grid2 xs>
                  <Typography variant="subtitle2">
                    Accepts SMS:
                    <Typography
                      sx={{ ml: 0.4 }}
                      variant="subtitle2"
                      component="span"
                      color={
                        getValues(
                          props?.field?.dependencies?.smsOptedIn || 'undefined',
                        )
                          ? 'success'
                          : 'error'
                      }>
                      {getValues(
                        props?.field?.dependencies?.smsOptedIn || 'undefined',
                      )
                        ? 'Yes'
                        : 'No'}
                    </Typography>
                  </Typography>
                </Grid2>
                <Grid2 xs>
                  <Disclosure
                    setShowDisclosure={setShowDisclosure}
                    showDisclosure={showDisclosure}
                  />
                </Grid2>
              </Grid2>
            )}

            {insightPurchased &&
              props?.field?.type === 'INSIGHT_MANAGED' &&
              currentvalue && (
                <Typography variant="subtitle2">
                  Accepts SMS:
                  <Typography
                    sx={{ ml: 0.4 }}
                    variant="subtitle2"
                    component="span"
                    color={managedPhoneAcceptsSMS ? 'success' : 'error'}>
                    {managedPhoneAcceptsSMS ? 'Yes' : 'No'}
                  </Typography>
                </Typography>
              )}
          </ReadOnly>
        </Box>
      </Box>
    );
  }

  if (isHidden(props?.permission)) {
    return (
      <Box>
        <input
          type="hidden"
          name={props?.name || props?.field?.name}
          value={currentvalue}
        />
      </Box>
    );
  }

  let insightProps = null;

  if (insightPurchasedAndTypeInsight) {
    insightProps = {
      chip: getSMSSubscriptionAttributes(
        smsSubscriptionStatus,
        formatUSPhoneNumber(masterData.saPhoneNumber),
      ),
      smsSubscriptionStatus,
    };
  }

  const smsOptedInValue = useWatch({
    name: props.field?.dependencies?.smsOptedIn || 'undefined',
  });

  useEffect(() => {
    if (
      smsOptedInValue === null &&
      insightPurchased &&
      props?.field?.type === 'INSIGHT_FULL'
    ) {
      setValue(props.field?.dependencies?.smsOptedIn, false, {
        shouldDirty: false,
        shouldTouch: false,
        shouldValidate: false,
      });
    }
  }, [
    smsOptedInValue,
    setValue,
    props.field?.dependencies?.smsOptedIn,
    insightPurchased,
  ]);

  return (
    <Box ref={ref}>
      <Controller
        control={control}
        name={props.field.name}
        render={({ field: controllerField, fieldState }) => {
          return (
            <PhoneInput
              {...controllerField}
              country="US"
              insightProps={insightProps}
              id={props.field.name}
              international={false}
              inputComponent={PhoneTextField}
              error={fieldState.error?.message}
              value={controllerField.value ? `+1${controllerField.value}` : ''}
              onChange={val => controllerField.onChange(normalizeUS(val))}
            />
          );
        }}
      />
      {insightPurchased && props?.field?.type === 'INSIGHT_FULL' && (
        <Controller
          control={control}
          name={props.field?.dependencies?.smsOptedIn}
          render={({ field: controllerField }) => {
            return (
              <Grid2 container sx={{ alignItems: 'center' }}>
                <Grid2>
                  <RadioGroup
                    sx={{ ml: 1 }}
                    row
                    {...controllerField}
                    value={controllerField.value}>
                    <FormControlLabel
                      value="true"
                      control={<Radio size="small" sx={{ px: 0.2 }} />}
                      label={
                        <Tooltip
                          placement="top"
                          arrow
                          title="Opt-In to receive SMS notifications">
                          <span>SMS Opt-In</span>
                        </Tooltip>
                      }
                      sx={{ pr: 1 }}
                    />
                    <FormControlLabel
                      value="false"
                      control={
                        <Radio
                          size="small"
                          sx={{
                            px: 0.2,
                          }}
                        />
                      }
                      label={
                        <Tooltip
                          placement="top"
                          arrow
                          title="SMS messages will not be sent">
                          <span>No SMS</span>
                        </Tooltip>
                      }
                    />
                  </RadioGroup>
                </Grid2>
                <Grid2
                  sx={{
                    flexGrow: 1,
                    textAlign: 'right',
                    pr: 1,
                  }}>
                  <Disclosure
                    setShowDisclosure={setShowDisclosure}
                    showDisclosure={showDisclosure}
                  />
                </Grid2>
              </Grid2>
            );
          }}
        />
      )}
    </Box>
  );
});

export default observer(PhoneNumber);
